import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
const { FiCheck, FiX, FiAlertCircle, FiClipboard, FiFileText, FiClock, FiShield } = FiIcons;
import SafeIcon from '../common/SafeIcon';
import api from '../lib/api';
import toast from 'react-hot-toast';

export default function AgentReviewPanel() {
  const [pendingRuns, setPendingRuns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedRun, setSelectedRun] = useState(null);
  const [decisionNotes, setDecisionNotes] = useState('');
  const [overrideCategory, setOverrideCategory] = useState('');

  useEffect(() => {
    fetchPendingRuns();
  }, []);

  const fetchPendingRuns = async () => {
    try {
      const { data } = await api.get('/ai/pending-reviews');
      setPendingRuns(data || []);
    } catch (err) {
      console.error('Failed to fetch reviews:', err);
    } finally {
      setLoading(false);
    }
  };

  const submitDecision = async (decision) => {
    if (!selectedRun) return;

    // Mandatory Clinical Rationale Enforcement (v2.24.0)
    if (!decisionNotes.trim() || decisionNotes.length < 10) {
      toast.error('Mandatory: Clinical rationale must be at least 10 characters.');
      return;
    }

    if (decision === 'denied' && !overrideCategory) {
      toast.error('Mandatory: Please select an override category for forensic tracking.');
      return;
    }

    try {
      await api.post('/ai/review-decision', {
        executionId: selectedRun.correlation_id,
        decision,
        notes: decisionNotes,
        overrideCategory: decision === 'denied' ? overrideCategory : null
      });

      toast.success(`Clinical ${decision === 'approved' ? 'Confirmation' : 'Override'} Logged`);
      setPendingRuns(prev => prev.filter(r => r.id !== selectedRun.id));
      setSelectedRun(null);
      setDecisionNotes('');
      setOverrideCategory('');
    } catch (err) {
      toast.error('Failed to submit decision');
    }
  };

  if (loading) return <div className="p-8 text-center text-slate-400">Loading pending reviews...</div>;

  return (
    <div className="flex flex-col gap-6">
      <div className="lg:col-span-1 space-y-4">
        <h3 className="text-lg font-semibold text-white flex items-center gap-2">
          <SafeIcon icon={FiClock} className="text-amber-400" />
          Pending Reviews
        </h3>
        
        {pendingRuns.length === 0 ? (
          <div className="p-6 bg-slate-800/50 rounded-xl border border-slate-700 text-center text-slate-400">
            No pending overrides found.
          </div>
        ) : (
          pendingRuns.map(run => (
            <motion.div
              key={run.id}
              whileHover={{ scale: 1.02 }}
              onClick={() => setSelectedRun(run)}
              className={`p-4 rounded-xl border cursor-pointer transition-colors ${
                selectedRun?.id === run.id 
                  ? 'bg-indigo-500/10 border-indigo-500' 
                  : 'bg-slate-800/50 border-slate-700 hover:border-slate-600'
              }`}
            >
              <div className="flex justify-between items-start mb-2">
                <span className="text-xs font-mono text-slate-400">{run.correlation_id}</span>
                <span className="px-2 py-0.5 text-[10px] bg-red-500/20 text-red-400 rounded-full uppercase tracking-wider font-bold">
                  High Risk
                </span>
              </div>
              <p className="text-sm text-white font-medium mb-1">
                Neuro-Stability Interpretation
              </p>
              <p className="text-xs text-slate-400">Model: V2.9.5-Gold</p>
            </motion.div>
          ))
        )}
      </div>

      <div className="lg:col-span-2">
        <AnimatePresence mode="wait">
          {selectedRun ? (
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              className="bg-slate-800/50 rounded-2xl border border-slate-700 p-6"
            >
              <div className="flex justify-between items-center mb-6">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-indigo-500/10 rounded-xl flex items-center justify-center">
                    <SafeIcon icon={FiShield} className="text-indigo-500" />
                  </div>
                  <div>
                    <h4 className="text-xl font-bold text-white leading-none">Human-in-the-Loop Checkpoint</h4>
                    <p className="text-xs text-slate-500 mt-1">Audit Trace: {selectedRun.correlation_id}</p>
                  </div>
                </div>
                <button onClick={() => setSelectedRun(null)} className="p-2 hover:bg-slate-700 rounded-lg text-slate-400">
                  <SafeIcon icon={FiX} />
                </button>
              </div>

              <div className="bg-slate-900/50 border border-slate-700 rounded-xl p-6 mb-8">
                <div className="flex items-center gap-2 mb-4 text-indigo-400">
                  <SafeIcon icon={FiFileText} />
                  <span className="text-[10px] font-black uppercase tracking-[0.2em]">Generated Interpretation</span>
                </div>
                <p className="text-sm text-slate-300 leading-relaxed italic">
                  "{selectedRun.outputs?.[0]?.clinician_summary || "Interpretation content is currently being processed."}"
                </p>
              </div>

              <div className="space-y-6">
                <div>
                  <label className="text-xs font-black text-slate-500 uppercase tracking-widest block mb-3">Clinical Rationale (Mandatory)</label>
                  <textarea
                    value={decisionNotes}
                    onChange={(e) => setDecisionNotes(e.target.value)}
                    className="w-full bg-slate-900 border border-slate-700 rounded-xl p-4 text-white focus:ring-2 focus:ring-indigo-500 h-32 resize-none text-sm"
                    placeholder="Enter your clinical reasoning for this decision. This will be logged for regulatory compliance..."
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <button
                    onClick={() => submitDecision('approved')}
                    className="bg-emerald-600 hover:bg-emerald-500 text-white py-4 rounded-xl font-bold flex items-center justify-center gap-2 transition-all shadow-lg shadow-emerald-900/10"
                  >
                    <SafeIcon icon={FiCheck} />
                    Confirm Interpretation
                  </button>
                  <div className="space-y-2">
                    <select 
                      value={overrideCategory}
                      onChange={(e) => setOverrideCategory(e.target.value)}
                      className="w-full bg-slate-900 border border-slate-700 rounded-xl p-2 text-xs text-slate-400 outline-none"
                    >
                      <option value="">Select Override Reason...</option>
                      <option value="false_positive">False Positive</option>
                      <option value="clinical_disagreement">Clinical Disagreement</option>
                      <option value="data_quality_issue">Sensor Data Quality</option>
                      <option value="other">Other / Manual Adjustment</option>
                    </select>
                    <button
                      onClick={() => submitDecision('denied')}
                      className="w-full bg-rose-600/10 hover:bg-rose-600/20 text-rose-500 border border-rose-500/30 py-4 rounded-xl font-bold flex items-center justify-center gap-2 transition-all"
                    >
                      <SafeIcon icon={FiX} />
                      Override AI Output
                    </button>
                  </div>
                </div>
              </div>
            </motion.div>
          ) : (
            <div className="h-full flex flex-col items-center justify-center text-slate-600 border-2 border-dashed border-slate-800 rounded-3xl p-20 text-center">
              <SafeIcon icon={FiClipboard} className="text-5xl mb-6 opacity-20" />
              <h4 className="text-lg font-bold text-slate-400 mb-2">Awaiting Clinical Review</h4>
              <p className="text-sm max-w-xs">Select an interpretation from the roster to perform mandatory human-in-the-loop validation.</p>
            </div>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
}